import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  Heading,
  Text,
  Tooltip,
  VStack,
  Badge,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  Search,
  AlertTriangle,
  Workflow,
  Blocks,
  Siren,
  Calendar,
  Users,
  GitFork,
  MapPinHouse,
  CheckCircle,
  MessageSquarePlus,
  MessageSquare,
  HardDrive,
  Trash2,
} from 'lucide-react';
import { useCleanupProgress, CleanupStep } from '@/providers/cleanup-progress-provider';
import { useOptionalActions } from '@/providers/actions-provider';
import { useThreads } from '@/hooks/use-threads';
import type { ThreadMetadata } from '@/types/langgraph';

const PAGERDUTY_GREEN = '#06AC38';
const PAGERDUTY_BLUE = '#0052cc';
const GRAY_DEFAULT = '#A0AEC0';

// Icon mapping for cleanup steps. Aligned with the OBA (onboarding-progress)
// sidebar so a user moving between the two assistants sees the same glyph
// for the same resource. OCU-only steps (quick_discovery, incidents) keep
// their dedicated icons because no OBA equivalent exists.
const CLEANUP_STEP_ICONS: Record<string, React.ElementType> = {
  quick_discovery: Search,           // OCU-only
  services: GitFork,                  // OBA parity
  incidents: AlertTriangle,           // OCU-only
  incident_workflows: Workflow,       // OBA parity
  aiops: Blocks,                      // OBA parity
  escalation_policies: Siren,         // OBA parity
  schedules: Calendar,                // OBA parity
  teams: MapPinHouse,                 // OBA parity
  users: Users,                       // OBA parity
  backup: HardDrive,                  // OCU-only
  // Legacy step_name values still present in agent_steps for threads
  // created before the rename to the combined "users" step. Map them to
  // the new icon so old threads render the same glyph as new ones.
  responders: Users,
  stakeholders: Users,
};

// Display-name overrides applied on top of whatever the backend persisted
// in agent_steps.display_name. Keeps legacy threads readable after the
// "Users and Teams" merge without a DB migration.
const CLEANUP_STEP_DISPLAY_OVERRIDES: Record<string, string> = {
  responders: 'Users and Teams',
  stakeholders: 'Users and Teams',
  users: 'Users and Teams',
};

const getCleanupStepIcon = (stepName: string): React.ElementType => {
  return CLEANUP_STEP_ICONS[stepName] || CheckCircle;
};

const getCleanupStepDisplayName = (
  stepName: string,
  fallback: string,
): string => CLEANUP_STEP_DISPLAY_OVERRIDES[stepName] ?? fallback;


const formatRelativeTime = (ts: number) => {
  const days = Math.floor((Date.now() - ts) / 86400000);
  if (days <= 0) return `Today · ${new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
};

interface ThreadItemProps {
  thread: ThreadMetadata;
  isActive: boolean;
  onSwitch: (threadId: string) => void;
  onDelete: (threadId: string) => void;
}

const ThreadItem: React.FC<ThreadItemProps> = ({ thread, isActive, onSwitch, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      position="relative"
      w="full"
      borderRadius="md"
      bg={isActive ? 'blue.50' : 'transparent'}
      _hover={{ bg: isActive ? 'blue.50' : 'gray.50' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        as="button"
        textAlign="left"
        w="full"
        px={2}
        py={2}
        pr={hovered ? 8 : 2}
        borderRadius="md"
        onClick={() => onSwitch(thread.langGraphThreadId)}
      >
        <Text
          fontSize="sm"
          noOfLines={1}
          color={isActive ? 'blue.700' : 'gray.700'}
          fontWeight={isActive ? 'medium' : 'normal'}
        >
          {thread.title ?? 'Untitled'}
        </Text>
        <Text fontSize="xs" color="gray.400">
          {formatRelativeTime(thread.updatedAt)}
        </Text>
      </Box>
      {hovered && (
        <Box
          as="button"
          position="absolute"
          right={1}
          top="50%"
          transform="translateY(-50%)"
          p={1}
          borderRadius="md"
          color="gray.400"
          _hover={{ color: 'red.500', bg: 'red.50' }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onDelete(thread.langGraphThreadId);
          }}
          aria-label="Delete conversation"
        >
          <Trash2 size={13} />
        </Box>
      )}
    </Box>
  );
};

interface CleanupProgressSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const CleanupProgressSidebar: React.FC<CleanupProgressSidebarProps> = ({
  isCollapsed,
  onToggle,
}) => {
  const { steps, currentStep } = useCleanupProgress();
  const actionsCtx = useOptionalActions();
  const { threads, currentThreadId, switchThread, fetchRecentThreads, newChat, deleteThread } = useThreads();

  const { optimization: optimizeCount, cleanup: cleanupCount, review: reviewCount } =
    actionsCtx?.discoveredCounts ?? { optimization: 0, cleanup: 0, review: 0 };

  useEffect(() => {
    fetchRecentThreads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Force collapsed on mobile, respect prop on desktop
  const isMobile = useBreakpointValue({ base: true, md: false });
  const forceCollapsed = isMobile ? true : isCollapsed;

  // Calculate progress percentage
  const completedCount = steps.filter((step) => step.status === 'Complete').length;
  const progressPercentage =
    steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  return (
    <Box
      w={{ base: '50px', md: forceCollapsed ? '60px' : '280px' }}
      minW={{ base: '50px', md: '100px' }}
      h="100%"
      bg="white"
      borderRight="1px solid"
      borderColor="gray.200"
      position="relative"
      transition="all 0.3s ease-in-out"
      display="flex"
      flexDirection="column"
    >
      {/* Scrollable Content Area */}
      <Box flex="1" overflowY="auto" overflowX="hidden" p={{ base: 2, md: forceCollapsed ? 2 : 6 }}>
        {forceCollapsed ? (
          /* Collapsed State */
          <VStack spacing={0} align="center" pt={4}>
            {/* Progress Badge */}
            <Badge
              colorScheme="green"
              fontSize="sm"
              fontWeight="bold"
              borderRadius="full"
              px={4}
              py={1}
              minW="44px"
              textAlign="center"
              mb={4}
            >
              {progressPercentage}%
            </Badge>

            {/* Vertical Timeline Icons */}
            <VStack spacing={0} align="center">
              {steps.map((step, index) => {
                const isCurrentStep = currentStep?.step_name === step.step_name;
                const isCompleted = step.status === 'Complete';
                const isLastStep = index === steps.length - 1;
                const StepIcon = getCleanupStepIcon(step.step_name);

                // Determine icon color based on state
                const iconColor = isCompleted
                  ? PAGERDUTY_GREEN
                  : isCurrentStep
                    ? PAGERDUTY_BLUE
                    : GRAY_DEFAULT;

                return (
                  <Box key={step.step_name} position="relative">
                    <Box py={4}>
                      <StepIcon size={20} color={iconColor} />
                    </Box>

                    {/* Vertical connecting line */}
                    {!isLastStep && (
                      <Box
                        position="absolute"
                        left="50%"
                        top="28px"
                        my={4}
                        w="2px"
                        h="16px"
                        bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
                        transform="translateX(-50%)"
                      />
                    )}
                  </Box>
                );
              })}
            </VStack>

            <Divider my={3} borderColor="gray.200" />
            <Tooltip label="New Analysis" placement="right">
              <Box
                as="button"
                aria-label="New Analysis"
                onClick={newChat}
                w="36px"
                h="36px"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: 'gray.100' }}
              >
                <MessageSquarePlus size={18} color={PAGERDUTY_BLUE} />
              </Box>
            </Tooltip>
          </VStack>
        ) : (
          /* Expanded State */
          <>
            <Heading size="md" mb={4}>
              Optimize & Cleanup Progress
            </Heading>

            {/* Progress Bar */}
            <Box mb={4}>
              <Badge
                colorScheme="green"
                fontSize="sm"
                fontWeight="bold"
                borderRadius="full"
                px={6}
                py={1}
                mb={2}
              >
                {progressPercentage}% Complete
              </Badge>
              <Flex gap={2}>
                {[
                  { count: optimizeCount, label: 'Optimize', color: 'purple' },
                  { count: cleanupCount,  label: 'Cleanup',  color: 'teal'   },
                  { count: reviewCount,   label: 'Manual',   color: 'orange' },
                ].map(({ count, label, color }) => (
                  <Flex
                    key={label}
                    flex={1}
                    direction="column"
                    align="center"
                    bg={`${color}.50`}
                    borderRadius="md"
                    py={1}
                    px={1}
                  >
                    <Text fontSize="sm" fontWeight="bold" color={`${color}.600`} lineHeight="1">
                      {count}
                    </Text>
                    <Text fontSize="9px" color={`${color}.500`} mt="2px" lineHeight="1">
                      {label}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Box>

            {/* Steps List */}
            <VStack align="stretch" spacing={0}>
              {steps.map((step, index) => {
                const isCurrentStep = currentStep?.step_name === step.step_name;
                const isCompleted = step.status === 'Complete';
                const isLastStep = index === steps.length - 1;
                const StepIcon = getCleanupStepIcon(step.step_name);

                // Determine icon color based on state
                const iconColor = isCompleted
                  ? PAGERDUTY_GREEN
                  : isCurrentStep
                    ? PAGERDUTY_BLUE
                    : GRAY_DEFAULT;

                const canScroll = isCompleted || isCurrentStep;
                const handleStepClick = () => {
                  if (!canScroll) return;
                  window.dispatchEvent(
                    new CustomEvent('scroll-to-cleanup-step', { detail: { stepName: step.step_name } }),
                  );
                };

                return (
                  <Box key={step.step_name} position="relative">
                    <Flex
                      align="center"
                      gap={3}
                      py={2}
                      onClick={handleStepClick}
                      cursor={canScroll ? 'pointer' : 'default'}
                      borderRadius="md"
                      px={1}
                      _hover={canScroll ? { bg: 'gray.50' } : {}}
                      transition="background 0.15s"
                    >
                      <Box position="relative" py={2} flexShrink={0}>
                        <StepIcon size={20} color={iconColor} />
                      </Box>

                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight={isCurrentStep ? 'semibold' : 'normal'}
                          color={isCompleted || isCurrentStep ? 'gray.800' : 'gray.500'}
                        >
                          {getCleanupStepDisplayName(step.step_name, step.display_name)}
                        </Text>
                        {isCurrentStep && step.issues_found > 0 && (
                          <Text fontSize="xs" color="orange.600">
                            {step.issues_found} issues found
                          </Text>
                        )}
                      </Box>
                    </Flex>

                    {/* Vertical connecting line */}
                    {!isLastStep && (
                      <Box
                        position="absolute"
                        left="10px"
                        top="28px"
                        my={4}
                        w="2px"
                        h="16px"
                        bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
                        transform="translateX(-50%)"
                      />
                    )}
                  </Box>
                );
              })}
            </VStack>

            <Divider my={4} borderColor="gray.200" />

            {/* Recent threads header */}
            <Flex justify="space-between" align="center" mb={3}>
              <Tooltip label="Your last 5 analyses" placement="top" hasArrow>
                <Flex align="center" cursor="default">
                  <MessageSquare size={13} color={GRAY_DEFAULT} />
                  <Text
                    ml={2}
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Recent
                  </Text>
                </Flex>
              </Tooltip>
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap={1}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="xs"
                color={PAGERDUTY_BLUE}
                fontWeight="medium"
                _hover={{ bg: 'blue.50' }}
                onClick={newChat}
              >
                <MessageSquarePlus size={12} />
                New Analysis
              </Box>
            </Flex>

            {/* Thread list */}
            {threads.length === 0 ? (
              <Text fontSize="xs" color="gray.400" px={2}>
                No previous conversations
              </Text>
            ) : (
              <VStack align="stretch" spacing={0}>
                {threads.slice(0, 5).map((thread) => {
                  const isActive = thread.langGraphThreadId === currentThreadId;
                  return (
                    <ThreadItem
                      key={thread.langGraphThreadId}
                      thread={thread}
                      isActive={isActive}
                      onSwitch={switchThread}
                      onDelete={deleteThread}
                    />
                  );
                })}
              </VStack>
            )}
          </>
        )}
      </Box>

      {/* Toggle Button - Hidden on mobile */}
      <Box
        position="absolute"
        top="50%"
        right={0}
        transform="translateY(-50%)"
        zIndex={10}
        display={{ base: 'none', md: 'block' }}
      >
        <Box
          as="button"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onToggle}
          w="24px"
          h="60px"
          bg="white"
          borderRadius="8px 0 0 8px"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          transition="all 0.2s"
        >
          <Box w="2px" h="24px" bg="gray.400" borderRadius="full" />
        </Box>
      </Box>
    </Box>
  );
};
